import { useMediaQuery } from '@mui/material'
import { DebouncedFuncLeading } from 'lodash'
import { isMobile } from 'mobile-device-detect'
import { CSSProperties, forwardRef, ReactElement, UIEvent, UIEventHandler, useEffect, useRef } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeGrid } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import theme from '../../../../config/theme'
import { BucketMedia } from '../../../../graphql/generated'
import { activeScrollingVar, showPublicShareUpsellModalVar } from '../../../../providers/apollo/cache'
import PublicShareUpsellModal from '../../modals/PublicShareUpsellModal'
import DesktopCell from './DesktopCell'

interface Props {
  items: BucketMedia[]
  fetchMore: DebouncedFuncLeading<() => Promise<void>>
  hasMore: boolean
  showUpsellModal: boolean
  setShowUpsellModal: (show: boolean) => void
}

interface OuterElementProps {
  children: ReactElement
  style: CSSProperties
  onScroll: UIEventHandler<HTMLDivElement>
}

interface InnerElementProps {
  children: ReactElement
  style: CSSProperties
}

const outerElementType = forwardRef<HTMLDivElement, OuterElementProps>(({ children, style, onScroll }, ref) => {
  const lastScrollPosition = useRef(0)

  const handleScroll = (e: UIEvent<HTMLDivElement, globalThis.UIEvent>) => {
    const positionFromTop = e.currentTarget.scrollTop

    if (positionFromTop > 100 && positionFromTop > lastScrollPosition.current) {
      activeScrollingVar('down')
    } else {
      activeScrollingVar('up')
    }
    lastScrollPosition.current = e.currentTarget.scrollTop
    onScroll(e)
  }

  return (
    <div id="gallery" style={{ ...style, overflowX: 'hidden' }} onScroll={handleScroll} ref={ref}>
      {children}
    </div>
  )
})

const innerElementType = forwardRef<HTMLDivElement, InnerElementProps>(({ style, children }, ref) => (
  <div data-test="grid" ref={ref} style={{ ...style, height: Number(style.height) + 60 }}>
    {children}
  </div>
))

export default function InfiniteGrid({ items, fetchMore, hasMore, showUpsellModal, setShowUpsellModal }: Readonly<Props>) {
  const scrollbarOffset = isMobile ? 0 : 14
  const isItemLoaded = (index: number) => !!items[index]
  const fixedSizeGridRef = useRef<FixedSizeGrid | null>(null)
  const sm = useMediaQuery(theme.breakpoints.up('sm'))
  const md = useMediaQuery(theme.breakpoints.up('md'))
  const lg = useMediaQuery(theme.breakpoints.up('lg'))
  const xl = useMediaQuery(theme.breakpoints.up('xl'))

  // Unselect all images when unmounting the gallery
  useEffect(() => {
    activeScrollingVar(null)

    return () => {
      activeScrollingVar(null)
    }
  }, [])

  const loadMoreItems = async (_startIndex: number, stopIndex: number) => {
    if (!items[stopIndex]) {
      fetchMore()
    }
  }

  let zoom = 2

  if (xl) {
    zoom = 5
  } else if (lg) {
    zoom = 4
  } else if (md) {
    zoom = 3
  } else if (sm) {
    zoom = 2
  }

  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <AutoSizer>
        {({ height, width }) => {
          if (!width || !height) return <div />

          return (
            <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={items.length + Number(hasMore)} loadMoreItems={loadMoreItems}>
              {({ onItemsRendered, ref }) => (
                <FixedSizeGrid
                  columnCount={zoom}
                  columnWidth={Math.floor(width / zoom) - Math.floor(scrollbarOffset / zoom)}
                  height={height}
                  innerElementType={innerElementType}
                  itemData={items}
                  ref={(gridRef) => {
                    fixedSizeGridRef.current = gridRef
                    ref(gridRef)
                  }}
                  outerElementType={outerElementType}
                  rowCount={Math.ceil(items.length / zoom)}
                  rowHeight={Math.floor(width / zoom) - Math.floor(scrollbarOffset / zoom) + 32}
                  width={width}
                  onItemsRendered={(gridProps) => {
                    const overscanStartIndex = gridProps.overscanRowStartIndex * zoom
                    const overscanStopIndex = gridProps.overscanRowStopIndex * zoom
                    const visibleStartIndex = gridProps.visibleRowStartIndex * zoom
                    const visibleStopIndex = gridProps.visibleRowStopIndex * zoom

                    onItemsRendered({ overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex })
                  }}
                >
                  {(props) => DesktopCell({ ...props, numCols: zoom })}
                </FixedSizeGrid>
              )}
            </InfiniteLoader>
          )
        }}
      </AutoSizer>
      {showUpsellModal && (
        <PublicShareUpsellModal
          open={showUpsellModal}
          onClose={() => {
            setShowUpsellModal(false)
            showPublicShareUpsellModalVar(false)
          }}
        />
      )}
    </div>
  )
}
